import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { WorkOrdersOverviewService } from '../../../services/work-orders-overview.service';
import { WorkshopFilterService } from '../../../../../_shared/services/workshop-filter.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import {
  ServiceItemView,
  UpdateWorkOrderDetailsRequest,
  WorkOrderPartRequirement,
} from '../../../models/work-orders-overview.models';
import {
  WorkOrderServiceType,
  WorkOrderServiceTypeLabels,
  WorkOrderPriority,
  WORK_ORDER_MISSED_SERVICE_LABEL,
} from '../../../enums/work-order.enum';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WorkshopEventScheduleData } from '../../../models/work-order.models';
import { WorkshopOption, Option } from '../../../types/types';
import { PartInventoryItem } from '../../../../../parts-and-inventory/parts/models/inventory-overview.model';
import { PartsAndInventoryService } from '../../../../../parts-and-inventory/parts/services/parts-and-inventory.service';

@Component({
  selector: 'app-workshop-event-schedule-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    InfiniteScrollModule,
    FlatpickrDatePickerComponent,
    TranslocoDirective,
    TranslocoPipe,
  ],
  templateUrl: './event-schedule-modal.component.html',
  styleUrl: './event-schedule-modal.component.scss',
})
export class WorkshopEventScheduleModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() eventData?: WorkshopEventScheduleData;
  @Input() accountIds?: number[];

  @ViewChild('pillScroller') pillScroller?: ElementRef<HTMLDivElement>;

  private workOrdersService = inject(WorkOrdersOverviewService);
  private workshopFilterService = inject(WorkshopFilterService);
  private partsAndInventoryService = inject(PartsAndInventoryService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancelSubject = new Subject<void>();

  form: FormGroup;
  isLoading = false;
  isServiceItemsLoading = false;
  isEditingDetails: boolean = false;
  scheduledDateValue: string = '';

  private readonly partsPageSize: number = 20;
  private readonly searchDebounceMs: number = 300;

  // Inline "search and add new part" dropdown
  partsSearchTerm: string = '';
  partsSearchResults: PartInventoryItem[] = [];
  partsDropdownOpen: boolean = false;
  isSearchingParts: boolean = false;
  isLoadingMoreParts: boolean = false;

  // Quantity typed into a result row before it is added, keyed by part id
  private draftQuantities = new Map<number, number>();

  private partsPage: number = 1;
  private partsTotalPages: number = 0;
  private hasMoreParts: boolean = true;
  private partsRequestSequence: number = 0;
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Selected parts pill scroller
  showAllParts: boolean = false;
  showPillLeftArrow: boolean = false;
  showPillRightArrow: boolean = false;

  // Parts already issued to this work order, as returned by the last load
  private issuedParts: ServiceItemView[] = [];

  // Selection as it stood when the modal loaded, restored when editing is cancelled
  private baselineParts: ServiceItemView[] = [];

  // Work order data
  workOrderId: number = 0;
  workshopId: number | null = null;
  serviceType: WorkOrderServiceType | null = null;
  priority: WorkOrderPriority = WorkOrderPriority.Medium;

  maintenanceTypes: Option[] = [
    {
      label: WorkOrderServiceTypeLabels[WorkOrderServiceType.MinorService],
      value: WorkOrderServiceType.MinorService.toString(),
    },
    {
      label:
        WorkOrderServiceTypeLabels[WorkOrderServiceType.IntermediateService],
      value: WorkOrderServiceType.IntermediateService.toString(),
    },
    {
      label: WorkOrderServiceTypeLabels[WorkOrderServiceType.MajorService],
      value: WorkOrderServiceType.MajorService.toString(),
    },
  ];

  workshops: WorkshopOption[] = [];

  selectedMaintenanceType = '';
  selectedServiceCentre = '';

  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder
  ) {
    this.form = this.fb.group({
      asset: [{ value: '', disabled: true }],
      make: [{ value: '', disabled: true }],
      model: [{ value: '', disabled: true }],
      odometer: [{ value: '', disabled: true }],
      maintenanceType: ['', Validators.required],
      serviceCentre: ['', Validators.required],
      scheduledDate: ['', Validators.required],
      serviceItems: [[] as ServiceItemView[]],
    });
  }

  ngOnInit(): void {
    this.initializeFormWithEventData();
    this.loadWorkshops();
    void this.loadIssuedPartsAsync();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updatePillArrows());
  }

  ngOnDestroy(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadWorkshops(): Promise<void> {
    try {
      const response = await this.workshopFilterService.getWorkshopList(
        [],
        this.cancelSubject
      );
      this.workshops = (response.data ?? []).map((w) => ({
        id: w.id,
        name: w.name,
      }));
      this.resolveServiceCentreSelection();
    } catch (error) {
      this.resolveServiceCentreSelection();
    }
  }


  private async loadIssuedPartsAsync(): Promise<void> {
    if (!this.workOrderId) {
      return;
    }

    this.isServiceItemsLoading = true;

    try {
      const response =
        await this.partsAndInventoryService.getIssuedPartsByWorkOrderAsync(
          this.workOrderId,
          this.cancelSubject
        );

      this.issuedParts = (response.data ?? []).map((part) => ({
        partId: part.partId,
        partName: part.partName,
        partNumber: part.partNumber,
        quantity: 1,
      }));

      const merged = this.mergeParts(this.selectedItems, this.issuedParts);
      this.baselineParts = merged.map((part) => ({ ...part }));
      this.setServiceItems(merged);
      this.isServiceItemsLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error);
      this.isServiceItemsLoading = false;
    }
  }

  private mergeParts(
    existing: ServiceItemView[],
    incoming: ServiceItemView[]
  ): ServiceItemView[] {
    const merged = [...existing];

    incoming.forEach((part) => {
      const match = merged.find((x) => x.partName === part.partName);

      if (match) {
        match.partId = match.partId ?? part.partId;
        match.partNumber = match.partNumber ?? part.partNumber;
        match.location = match.location ?? part.location;
      } else {
        merged.push({ ...part });
      }
    });

    return merged;
  }

  private setServiceItems(serviceItems: ServiceItemView[]): void {
    this.form.get('serviceItems')?.setValue(serviceItems);
    setTimeout(() => this.updatePillArrows());
  }

  private initializeFormWithEventData(): void {
    if (this.eventData) {
      this.workOrderId = this.eventData.workOrderId ?? this.eventData.id ?? 0;
      this.form.get('asset')?.setValue(this.eventData.assetName);
      this.form.get('make')?.setValue(this.eventData.make ?? '');
      this.form.get('model')?.setValue(this.eventData.model ?? '');
      this.form.get('odometer')?.setValue(this.eventData.odometer ?? '');

      const maintenanceType = this.mapEventTypeToMaintenanceType(
        this.eventData.type
      );
      this.setMaintenanceType(
        maintenanceType,
        this.mapLabelToServiceType(maintenanceType)
      );

      const serviceCentre =
        this.eventData.workshop || this.eventData.serviceCentre || '';
      if (serviceCentre) {
        const workshop = this.findWorkshopByName(serviceCentre);
        this.setServiceCentre(serviceCentre, workshop?.id ?? null);
      }

      if (this.eventData.scheduledDate) {
        this.scheduledDateValue = this.formatDateForPicker(
          new Date(this.eventData.scheduledDate)
        );
      } else {
        this.scheduledDateValue = '';
      }
      this.form.get('scheduledDate')?.setValue(this.scheduledDateValue);

      // Seeded from the calendar payload (names only), then merged with the issued parts
      const seededParts = (this.eventData.serviceItems ?? []).map((partName) => ({
        partName,
        quantity: 1,
      }));
      this.baselineParts = seededParts.map((part) => ({ ...part }));
      this.setServiceItems(seededParts);
    }
  }

  private mapEventTypeToMaintenanceType(type?: string): string {
    switch (type) {
      case 'minor':
        return WorkOrderServiceTypeLabels[WorkOrderServiceType.MinorService];
      case 'intermediate':
        return WorkOrderServiceTypeLabels[
          WorkOrderServiceType.IntermediateService
        ];
      case 'major':
        return WorkOrderServiceTypeLabels[WorkOrderServiceType.MajorService];
      case 'missed':
        return WORK_ORDER_MISSED_SERVICE_LABEL;
      default:
        return WorkOrderServiceTypeLabels[WorkOrderServiceType.MinorService];
    }
  }

  private mapLabelToServiceType(label: string): WorkOrderServiceType | null {
    for (const [key, value] of Object.entries(WorkOrderServiceTypeLabels)) {
      if (value === label) {
        return parseInt(key) as WorkOrderServiceType;
      }
    }
    return null;
  }

  onScheduledDateChange(value: string | undefined): void {
    this.scheduledDateValue = value || '';
    this.form.get('scheduledDate')?.setValue(value);
    this.form.get('scheduledDate')?.markAsTouched();
  }

  get assetLabel(): string {
    return this.form.get('asset')?.value || this.transloco.translate('workOrder.modals.eventSchedule.noAsset');
  }

  get maintenanceTypeLabel(): string {
    return this.selectedMaintenanceType
      ? this.transloco.translate(this.selectedMaintenanceType)
      : this.transloco.translate('workOrder.modals.eventSchedule.selectMaintenanceType');
  }

  get serviceCentreLabel(): string {
    return this.selectedServiceCentre || this.transloco.translate('workOrder.modals.eventSchedule.selectServiceCentre');
  }

  get selectedItems(): ServiceItemView[] {
    return (this.form.get('serviceItems')?.value as ServiceItemView[]) ?? [];
  }

  partMetaLabel(part: ServiceItemView): string {
    return part.location || '';
  }

  partPillTitle(part: ServiceItemView): string {
    const title = [part.partName, part.partNumber].filter(Boolean).join(' ');
    const meta = this.partMetaLabel(part);
    return meta ? `${title} (${meta})` : title;
  }

  onMaintenanceTypeSelect(option: Option, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const serviceType = parseInt(option.value) as WorkOrderServiceType;
    this.setMaintenanceType(option.label, serviceType);
    this.form.get('maintenanceType')?.markAsTouched();

    // Auto-set priority based on service type
    switch (serviceType) {
      case WorkOrderServiceType.MajorService:
        this.priority = WorkOrderPriority.High;
        break;
      case WorkOrderServiceType.IntermediateService:
        this.priority = WorkOrderPriority.Medium;
        break;
      case WorkOrderServiceType.MinorService:
        this.priority = WorkOrderPriority.Low;
        break;
    }
  }

  onServiceCentreSelect(workshop: WorkshopOption, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.setServiceCentre(workshop.name, workshop.id);
    this.form.get('serviceCentre')?.markAsTouched();
  }

  toggleEditDetails(): void {
    this.isEditingDetails = !this.isEditingDetails;
  }

  isPartSelected(partName: string): boolean {
    return this.selectedItems.some((part) => part.partName === partName);
  }

  removeItem(partName: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.setServiceItems(
      this.selectedItems.filter((part) => part.partName !== partName)
    );
    this.form.get('serviceItems')?.markAsDirty();
  }

  getRowQuantity(part: PartInventoryItem): number {
    const selected = this.selectedItems.find(
      (x) => x.partName === part.partName
    );

    return selected?.quantity ?? this.draftQuantities.get(part.partId) ?? 1;
  }

  changeRowQuantity(part: PartInventoryItem, delta: number): void {
    const quantity = Math.max(1, this.getRowQuantity(part) + delta);
    const selected = this.selectedItems.find(
      (x) => x.partName === part.partName
    );

    if (selected) {
      selected.quantity = quantity;
      this.form.get('serviceItems')?.markAsDirty();
      return;
    }

    this.draftQuantities.set(part.partId, quantity);
  }

  addPart(part: PartInventoryItem): void {
    if (this.isPartSelected(part.partName)) {
      return;
    }

    this.setServiceItems([
      ...this.selectedItems,
      {
        partId: part.partId,
        partName: part.partName,
        partNumber: part.partNumber,
        location: part.location,
        quantity: this.getRowQuantity(part),
      },
    ]);
    this.form.get('serviceItems')?.markAsDirty();
    this.draftQuantities.delete(part.partId);
  }

  onPartsSearchFocus(): void {
    if (this.partsDropdownOpen) {
      return;
    }

    this.partsDropdownOpen = true;
    void this.resetAndLoadPartsAsync();
  }

  onPartsSearchChange(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.partsDropdownOpen = true;
    this.isSearchingParts = true;

    this.searchDebounceTimer = setTimeout(() => {
      this.searchDebounceTimer = null;
      void this.resetAndLoadPartsAsync();
    }, this.searchDebounceMs);
  }

  onPartsScrollDown(): void {
    if (!this.hasMoreParts || this.isLoadingMoreParts || this.isSearchingParts) {
      return;
    }

    void this.loadPartsPageAsync(false);
  }

  closePartsDropdown(): void {
    this.partsDropdownOpen = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closePartsDropdown();
  }

  toggleShowAllParts(): void {
    this.showAllParts = !this.showAllParts;
    setTimeout(() => this.updatePillArrows());
  }

  onPillScroll(): void {
    this.updatePillArrows();
  }

  scrollPills(direction: 1 | -1): void {
    const element = this.pillScroller?.nativeElement;
    if (!element) {
      return;
    }

    const amount = Math.max(120, Math.floor(element.clientWidth * 0.8));
    element.scrollBy({ left: direction * amount, behavior: 'smooth' });
    setTimeout(() => this.updatePillArrows());
  }

  @HostListener('window:resize')
  onResize(): void {
    setTimeout(() => this.updatePillArrows());
  }

  private updatePillArrows(): void {
    const element = this.pillScroller?.nativeElement;

    if (!element || this.showAllParts) {
      this.showPillLeftArrow = false;
      this.showPillRightArrow = false;
      return;
    }

    const maxScroll = element.scrollWidth - element.clientWidth;
    this.showPillLeftArrow = element.scrollLeft > 0;
    this.showPillRightArrow =
      maxScroll > 0 && element.scrollLeft < maxScroll - 1;
  }

  private async resetAndLoadPartsAsync(): Promise<void> {
    this.partsPage = 1;
    this.partsTotalPages = 0;
    this.hasMoreParts = true;
    this.partsSearchResults = [];

    await this.loadPartsPageAsync(true);
  }

  private async loadPartsPageAsync(isInitialLoad: boolean): Promise<void> {
    if (!isInitialLoad && (!this.hasMoreParts || this.isLoadingMoreParts)) {
      return;
    }

    const requestId = ++this.partsRequestSequence;

    if (isInitialLoad) {
      this.isSearchingParts = true;
    } else {
      this.isLoadingMoreParts = true;
    }

    try {
      const response =
        await this.partsAndInventoryService.getPaginatedPartsInventoryAsync({
          page: this.partsPage,
          pageSize: this.partsPageSize,
          searchTerm: this.partsSearchTerm?.trim() ?? '',
          accountIds: this.accountIds ?? [],
          warehouseIds: [],
          categoryIds: [],
        });

      if (requestId !== this.partsRequestSequence) {
        return;
      }

      const results = response.data?.results ?? [];

      this.partsSearchResults = isInitialLoad
        ? results
        : [...this.partsSearchResults, ...results];
      this.partsTotalPages = response.data?.totalPages ?? 0;
      this.hasMoreParts = this.partsPage < this.partsTotalPages;

      if (this.hasMoreParts) {
        this.partsPage += 1;
      }
      if (requestId === this.partsRequestSequence) {
        if (isInitialLoad) {
          this.isSearchingParts = false;
        } else {
          this.isLoadingMoreParts = false;
        }
      }
    } catch (error) {
      if (requestId === this.partsRequestSequence) {
        this.errorHandlerService.handleError(error);
        this.partsSearchResults = [];
      if (requestId === this.partsRequestSequence) {
        if (isInitialLoad) {
          this.isSearchingParts = false;
        } else {
          this.isLoadingMoreParts = false;
          }
        }
      }
    }
  }

  onCancel(): void {
    this.resetServiceItems();
    this.isEditingDetails = false;
    this.closePartsDropdown();
  }

  private resetServiceItems(): void {
    this.draftQuantities.clear();
    this.setServiceItems(this.baselineParts.map((part) => ({ ...part })));
    this.form.get('serviceItems')?.markAsPristine();
  }

  closeModal(): void {
    this.activeModal.close();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.workOrderId) {
      Object.keys(this.form.controls).forEach((k) =>
        this.form.get(k)?.markAsTouched()
      );
      return;
    }

    this.isLoading = true;

    try {
      // Parse scheduled date
      let scheduledDate: Date | undefined;
      if (this.scheduledDateValue) {
        scheduledDate = this.parseScheduledDate(this.scheduledDateValue);
      }

      const request: UpdateWorkOrderDetailsRequest = {
        workOrderId: this.workOrderId,
        serviceType: this.serviceType ?? undefined,
        workshopId: this.workshopId ?? undefined,
        scheduledDate,
        priority: this.priority,
        partsRequired: this.selectedItems.map<WorkOrderPartRequirement>(
          (part) => ({
            partId: part.partId,
            partName: part.partName,
            quantity: part.quantity,
          })
        ),
      };

      await this.workOrdersService.updateWorkOrderDetailsAsync(
        request,
        this.cancelSubject
      );

      this.toastr.success(this.transloco.translate('workOrder.toasts.eventScheduleUpdated'));
      this.activeModal.close(request);
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error);
      this.isLoading = false;
    }
  }

  private setMaintenanceType(
    label: string,
    serviceType: WorkOrderServiceType | null
  ): void {
    this.selectedMaintenanceType = label;
    this.serviceType = serviceType;
    this.form.get('maintenanceType')?.setValue(label);
  }

  private setServiceCentre(name: string, workshopId: number | null): void {
    this.selectedServiceCentre = name;
    this.workshopId = workshopId;
    this.form.get('serviceCentre')?.setValue(name);
  }

  private resolveServiceCentreSelection(): void {
    const serviceCentre =
      this.eventData?.workshop ||
      this.eventData?.serviceCentre ||
      this.selectedServiceCentre;

    if (!serviceCentre) {
      return;
    }

    const workshop = this.findWorkshopByName(serviceCentre);
    this.setServiceCentre(serviceCentre, workshop?.id ?? this.workshopId);
  }

  private findWorkshopByName(name: string): WorkshopOption | undefined {
    const normalize = (value: string): string =>
      value.trim().replace(/\s+/g, ' ').toLowerCase();
    const normalizedName = normalize(name);

    return this.workshops.find(
      (workshop) => normalize(workshop.name) === normalizedName
    );
  }

  private formatDateForPicker(date: Date): string {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private parseScheduledDate(value: string): Date {
    const [datePart, timePart] = value.split(' ');
    const [year, month, day] = datePart.split('/').map(Number);
    const [hours, minutes] = (timePart || '00:00').split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }
}
